"use client";

import {
  businessFiltersAtom,
  businessMapBoundsAtom,
} from "@/app/(saas)/(app)/businesses/_components/filters/state";
import { api } from "@/lib/api-client";
import { useAtomValue } from "jotai";
import { useEffect, useMemo } from "react";

export function useListingFacetCounts() {
  const filters = useAtomValue(businessFiltersAtom);
  const mapBounds = useAtomValue(businessMapBoundsAtom);

  const input = useMemo(() => {
    return {
      bbox: mapBounds
        ? {
            sw: mapBounds.sw,
            ne: mapBounds.ne,
          }
        : undefined,
      businessTypeIds:
        filters.businessTypeIds.length > 0 ? filters.businessTypeIds : undefined,
      statuses: filters.statuses.length > 0 ? filters.statuses : undefined,
      listedBy: filters.listedBy.length > 0 ? filters.listedBy : undefined,
      saleTypes: filters.saleTypes.length > 0 ? filters.saleTypes : undefined,
      price:
        filters.priceRange.min !== null || filters.priceRange.max !== null
          ? {
              min: filters.priceRange.min ?? undefined,
              max: filters.priceRange.max ?? undefined,
            }
          : undefined,
      revenue:
        filters.revenueRange.min !== null || filters.revenueRange.max !== null
          ? {
              min: filters.revenueRange.min ?? undefined,
              max: filters.revenueRange.max ?? undefined,
            }
          : undefined,
      profit:
        filters.profitRange.min !== null || filters.profitRange.max !== null
          ? {
              min: filters.profitRange.min ?? undefined,
              max: filters.profitRange.max ?? undefined,
            }
          : undefined,
      sde:
        filters.sdeRange.min !== null || filters.sdeRange.max !== null
          ? {
              min: filters.sdeRange.min ?? undefined,
              max: filters.sdeRange.max ?? undefined,
            }
          : undefined,
      sdeMultiple:
        filters.sdeMultipleRange.min !== null ||
        filters.sdeMultipleRange.max !== null
          ? {
              min: filters.sdeMultipleRange.min ?? undefined,
              max: filters.sdeMultipleRange.max ?? undefined,
            }
          : undefined,
      revenueMultiple:
        filters.revenueMultipleRange.min !== null ||
        filters.revenueMultipleRange.max !== null
          ? {
              min: filters.revenueMultipleRange.min ?? undefined,
              max: filters.revenueMultipleRange.max ?? undefined,
            }
          : undefined,
      downPayment:
        filters.downPaymentRange.min !== null ||
        filters.downPaymentRange.max !== null
          ? {
              min: filters.downPaymentRange.min ?? undefined,
              max: filters.downPaymentRange.max ?? undefined,
            }
          : undefined,
      establishmentAge:
        filters.establishmentAgeRange.min !== null ||
        filters.establishmentAgeRange.max !== null
          ? {
              min: filters.establishmentAgeRange.min ?? undefined,
              max: filters.establishmentAgeRange.max ?? undefined,
            }
          : undefined,
      employeeCount:
        filters.employeeCountRange.min !== null ||
        filters.employeeCountRange.max !== null
          ? {
              min: filters.employeeCountRange.min ?? undefined,
              max: filters.employeeCountRange.max ?? undefined,
            }
          : undefined,
      ownerWorkedHours:
        filters.ownerWorkedHoursRange.min !== null ||
        filters.ownerWorkedHoursRange.max !== null
          ? {
              min: filters.ownerWorkedHoursRange.min ?? undefined,
              max: filters.ownerWorkedHoursRange.max ?? undefined,
            }
          : undefined,
      propertyIncluded: filters.propertyIncluded ?? undefined,
      buyerFinancingAvailable: filters.financingAvailable ?? undefined,
      sbaPrequalified: filters.sbaPrequalified ?? undefined,
      visaQualified: filters.visaQualified ?? undefined,
      includeMissingMultiples: filters.includeMissingMultiples,
      priceDirection: filters.priceDirection ?? undefined,
      priceChangedWithinDays: filters.priceChangedWithinDays ?? undefined,
      minPriceChangePercent: filters.minPriceChangePercent ?? undefined,
    };
  }, [filters, mapBounds]);

  const query = api.listings.getFacetCounts.useQuery(input, {
    enabled: true,
    retry: 2,
    refetchOnWindowFocus: false,
    staleTime: 20_000,
  });

  useEffect(() => {
    if (query.error) {
      console.error("[FacetCounts] query failed:", query.error.message);
    }
  }, [query.error]);

  return query;
}

